import { computed, ref, useFetch, watch } from '@nuxtjs/composition-api';
import useCheckout from '~/composable/checkout/useCheckout';
import useTlmCart from '~/composable/useTlmCart';
import useMitID from '~/composable/useMitID';
import useSubscription from '~/composable/useSubscription';
import useTracking from '~/composable/tracking/useTracking';
import useCategories from '~/composable/useCategories';
import useFlow from '~/composable/checkout/config/useFlow';
import useCartQueries from '~/composable/useCartQueries';
import useShoppingList from '~/composable/useShoppingList';
import { CHECKOUT_FLOW, CUSTOMER_SUBSCRIPTION_TYPE } from '~/constants/CheckoutFlow';
import { OFFERED_TO_CUSTOMER_TYPE } from '~/constants/OfferConstants';
import { FLOW_STEP_STATUS } from '~/constants/FlowStepStatus';

// eslint-disable-next-line complexity
export default function useSubscriptionType (app, isActive) {
  const { state, checkoutType } = useCheckout(app)
  const { subscription, customerInfo } = state;
  const { permissionsAccepted } = customerInfo
  const { moveNumber, newNumber, changeSubscription, menu, changeSubscriptionOnMitTelmore } = subscription;
  const { containsKeepSubscription } = useTlmCart(app)
  const { getMitIdToken } = useMitID(app)
  const { getSubscriptionList } = useSubscription(app)
  const { cart, subscriptionList, installments, mixPoList, getItemAttributesForSubscriptionFromCart } = useTlmCart(app)
  const isActiveProp = computed(() => isActive)
  const { track } = useTracking()
  const { offeredToCustomerType } = useCategories(app.$hubble)
  const { validate: validateFlow, currentStep, getCurrentStepStatus, resetSignupValidation, setFlowStepStatus } = useFlow(app, state)

  const { cartOnlyContainsSubscription } = useCartQueries(app, cart)
  const { shoppingListExists } = useShoppingList(app)
  if (!shoppingListExists.value) {
    track('checkoutStepLoad', { currentStep, name: state?.panels[currentStep.value].heading })
  }

  const isNumberPortingFlow = () => {
    if (menu.value === CUSTOMER_SUBSCRIPTION_TYPE.MOVE_NUMBER) {
      return true
    }
    return getItemAttributesForSubscriptionFromCart('IsNumberPorting') === 'true'
  }

  const validate = async () => {
    const subscriptionSignUpData = customerInfo.subscriptionSignUpData
    subscriptionSignUpData.value.bundleId = subscriptionList.value.sku
    subscriptionSignUpData.value.shopReservationDTO = cartOnlyContainsSubscription.value
      ? null
      : {
          installments: installments?.value
        }
    subscriptionSignUpData.value.mixServices = mixPoList.value
    // Used by validations to decide whether to send mixServices or addedPlayAddOnServices.
    subscriptionSignUpData.value.checkoutFlow = checkoutType.value
    subscriptionSignUpData.value.isSimOnly = cartOnlyContainsSubscription.value
    await validateFlow(subscriptionSignUpData.value)
  }

  const validateOnLoad = async () => {
    if (isNumberPortingFlow()) {
      resetSignupValidation()
      return
    }
    await validate()
  }

  const menuItemsDefault = [
    {
      label: 'Opret nyt nummer',
      value: CUSTOMER_SUBSCRIPTION_TYPE.NEW_NUMBER,
      component: 'tlm-checkout-subscription-new-number',
      context: newNumber,
      customerType: OFFERED_TO_CUSTOMER_TYPE.NEW_CUSTOMER
    },
    {
      label: 'Flyt nuværende nummer',
      value: CUSTOMER_SUBSCRIPTION_TYPE.MOVE_NUMBER,
      component: 'tlm-checkout-subscription-move-number',
      context: moveNumber,
      customerType: OFFERED_TO_CUSTOMER_TYPE.NUMBER_PORTING
    },
    {
      label: 'Skift Telmore-abonnement',
      value: CUSTOMER_SUBSCRIPTION_TYPE.CHANGE_SUBSCRIPTION,
      component: 'tlm-checkout-subscription-change-subscription',
      context: changeSubscription,
      customerType: OFFERED_TO_CUSTOMER_TYPE.EXISTING
    }/*,
      {
        label: 'Behold abonnemement',
        value: CUSTOMER_SUBSCRIPTION_TYPE.KEEP_SUBSCRIPTION,
        component: 'tlm-checkout-subscription-keep-subscription',
        customerType: OFFERED_TO_CUSTOMER_TYPE.KEEP
      } */
  ];

  const showChangeSubscriptions = ref(false)
  const loadedSubscriptions = ref(false)
  const isLoggedIn = !!getMitIdToken()

  // get subscriptions
  const { fetch } = useFetch(async () => {
    if (!isLoggedIn) {
      showChangeSubscriptions.value = true
      loadedSubscriptions.value = true
      return
    }
    const res = await getSubscriptionList(getMitIdToken(), 'MIT_ID')
    const filteredSubscriptionList = !res?.value?.hasError ? res?.filter(bundle => bundle.bundleID.substr(0, 3) !== 'MBB') : undefined
    if (res?.value?.hasError || filteredSubscriptionList === undefined) {
      showChangeSubscriptions.value = false
    } else {
      changeSubscription.subscriptions = filteredSubscriptionList
      showChangeSubscriptions.value = filteredSubscriptionList.length > 0
    }
    loadedSubscriptions.value = true
  }, 'subscriptions')

  const getSubscriptionOnlyMenuItems = () => {
    const withoutChangeSubscriptionOption = [...menuItemsDefault].slice(0, 2)
    if (checkoutType.value !== CHECKOUT_FLOW.WITH_MOBILE_BROADBAND_SUBSCRIPTION) {
      withoutChangeSubscriptionOption.push({
        label: 'Skift Telmore-abonnement',
        value: CUSTOMER_SUBSCRIPTION_TYPE.CHANGE_SUBSCRIPTION_ON_MIT_TELMORE,
        component: 'tlm-checkout-subscription-change-subscription-on-mit-telmore',
        context: changeSubscriptionOnMitTelmore,
        customerType: OFFERED_TO_CUSTOMER_TYPE.EXISTING
      })
    }
    return withoutChangeSubscriptionOption
  }
  const getFilteredDefaultMenuItems = () => {
    return showChangeSubscriptions?.value ? menuItemsDefault : [...menuItemsDefault].slice(0, 2)
  }

  const menuItems = computed(() => {
    if (offeredToCustomerType?.value === null || offeredToCustomerType?.value === undefined) {
      // requirement: dont show the 'change subscription' menu item if the user doesnt have any and for sim only flow
      return cartOnlyContainsSubscription.value ? getSubscriptionOnlyMenuItems() : getFilteredDefaultMenuItems()
    } else {
      // filter out which customer type this product is allowed to be bought - based on category
      // TODO do it for each group id when we have multiple products in the basket
      return menuItemsDefault.filter(item => offeredToCustomerType?.value.includes(item.customerType))
    }
  })
  // eslint-disable-next-line complexity
  const hydrateSubscriptionMenuFromCart = () => {
    if (menu.value !== null && menu.value !== undefined) {
      return
    }
    if (getItemAttributesForSubscriptionFromCart('PrepaidAccountId') !== null && getItemAttributesForSubscriptionFromCart('PrepaidAccountId') !== undefined) {
      menu.value = CUSTOMER_SUBSCRIPTION_TYPE.CHANGE_SUBSCRIPTION
    } else if (getItemAttributesForSubscriptionFromCart('IsNumberPorting') === 'true') {
      menu.value = CUSTOMER_SUBSCRIPTION_TYPE.MOVE_NUMBER
      moveNumber.mobileNumber = getItemAttributesForSubscriptionFromCart('PhoneNumber')
      moveNumber.registerSimLater = getItemAttributesForSubscriptionFromCart('ICC') === ''
      moveNumber.initiateNpASAP = getItemAttributesForSubscriptionFromCart('IsImmediateNpRequest') === 'true'
      moveNumber.accepted = true
    } else if (getItemAttributesForSubscriptionFromCart('PhoneNumber')) {
      menu.value = CUSTOMER_SUBSCRIPTION_TYPE.NEW_NUMBER
      newNumber.numberSelected.phoneNumber = getItemAttributesForSubscriptionFromCart('PhoneNumber')
    }
    if (getItemAttributesForSubscriptionFromCart('PermissionEmail') === 'true') {
      permissionsAccepted.value = true
    }
  }
  // eslint-disable-next-line complexity
  watch(isActiveProp, () => {
    // all flows edit step except for mobile voice
    if (isActiveProp.value && checkoutType.value !== CHECKOUT_FLOW.WITH_MV_SUBSCRIPTION_ONLY && !cart.value.keepSubscriptionType) {
      hydrateSubscriptionMenuFromCart()
      validateOnLoad()
    } else if (getCurrentStepStatus.value === FLOW_STEP_STATUS.EDITING) {
      if (getItemAttributesForSubscriptionFromCart('PrepaidAccountId') !== null && getItemAttributesForSubscriptionFromCart('PrepaidAccountId') !== undefined) {
        menu.value = CUSTOMER_SUBSCRIPTION_TYPE.CHANGE_SUBSCRIPTION
      } else if (getItemAttributesForSubscriptionFromCart('IsNumberPorting') === 'true') {
        menu.value = CUSTOMER_SUBSCRIPTION_TYPE.MOVE_NUMBER
        moveNumber.mobileNumber = getItemAttributesForSubscriptionFromCart('PhoneNumber')
        moveNumber.registerSimLater = getItemAttributesForSubscriptionFromCart('ICC') === ''
        moveNumber.initiateNpASAP = getItemAttributesForSubscriptionFromCart('IsImmediateNpRequest') === 'true'
        moveNumber.accepted = true
      } else {
        menu.value = CUSTOMER_SUBSCRIPTION_TYPE.NEW_NUMBER
        newNumber.numberSelected.phoneNumber = getItemAttributesForSubscriptionFromCart('PhoneNumber')
      }
      permissionsAccepted.value = getItemAttributesForSubscriptionFromCart('PermissionEmail') === 'true'
    }
    track('checkoutStepLoad', { currentStep, name: state?.panels[currentStep.value].heading })
    $nuxt.$emit('editModeForSubscription')
  })
  if (checkoutType.value === CHECKOUT_FLOW.WITH_MV_SUBSCRIPTION_ONLY) {
    menu.value = getItemAttributesForSubscriptionFromCart('IsNumberPorting') === 'true' || getItemAttributesForSubscriptionFromCart('IsNumberPorting') === null
      ? CUSTOMER_SUBSCRIPTION_TYPE.MOVE_NUMBER
      : CUSTOMER_SUBSCRIPTION_TYPE.NEW_NUMBER
  } else if (!cart.value.keepSubscriptionType) {
    validateOnLoad()
  }
  watch(menu, () => {
    resetSignupValidation()
    const stepStatus = getCurrentStepStatus.value
    if (stepStatus === FLOW_STEP_STATUS.ERROR || stepStatus === FLOW_STEP_STATUS.FORM_SUBMITTED) {
      setFlowStepStatus(currentStep.value, FLOW_STEP_STATUS.EDITING)
    }
  })
  return {
    loadedSubscriptions,
    containsKeepSubscription,
    fetch,
    cart,
    isActiveProp,
    menuItems,
    moveNumber,
    newNumber,
    changeSubscription
  };
}
